import React, { Component, createRef } from 'react'
import _ from 'lodash'
import {
  Modal,
  Button,
  Input,
  Spin,
  Row,
  Col,
  Form,
  Icon,
  Progress,
  Result,
  message
} from 'antd'
import { withTranslation, Trans } from 'react-i18next'
import api from '../../services/api'
import { config, general } from '../../constants'
import { ModalStyles } from './CloneModal.ctrl'
import CourseDropzone from '../../views/Courses/Create/internal/CourseDropzone'
import { EditImageContainer } from '../../components/TextOnImage'
import { PopupMedia } from '../../views/Library/internal'
import { Editor } from '../../components/ImageEditor'
import TranslationSection from './TranslationSection'
import CloningSection from './CloningSection'
import './CloneModal.scss'
import langOptions from './langOptions'

const antIcon = <Icon type="loading" style={{ fontSize: 80 }} spin />
const AUDIO_PROVIDER_OPTIONS = [
  { key: 'generate-tts-v2', label: 'Sharelook TTS', service: 'TTS' },
  { key: 'elevenlabs', label: 'Sharelook Premium', service: 'ELEVENLABS' }
]

const normalizeAudioServiceValues = audioService => {
  if (Array.isArray(audioService)) return audioService
  if (!audioService) return []
  return String(audioService)
    .split(',')
    .map(service => service.trim())
    .filter(Boolean)
}

const normalizeAudioServiceName = service => {
  const normalizedService = String(service || '').trim()
  const compactService = normalizedService.toLowerCase().replace(/[\s_-]/g, '')

  if (compactService === 'tts' || compactService === 'sharelooktts') {
    return 'TTS'
  }

  if (
    compactService === 'elevenlabs' ||
    compactService === 'sharelookpremium'
  ) {
    return 'ELEVENLABS'
  }

  return normalizedService.toUpperCase()
}

const getAudioServiceProviders = user => {
  const audioService =
    user &&
    user.info &&
    user.info.organization &&
    user.info.organization.audio_service
  const providers = normalizeAudioServiceValues(audioService)
    .map(service => {
      const normalizedService = normalizeAudioServiceName(service)
      const matchedProvider = AUDIO_PROVIDER_OPTIONS.find(
        option =>
          option.service === normalizedService ||
          option.key === String(service || '').toLowerCase()
      )

      return matchedProvider ? matchedProvider.key : null
    })
    .filter(Boolean)

  const uniqueProviders = [...new Set(providers)]
  return uniqueProviders
}

const getDefaultAudioServiceProvider = user => {
  const providers = getAudioServiceProviders(user)
  return providers.length ? providers[0] : null
}

const getSupportedTextLanguageCode = languageCode => {
  const rawLanguage = String(languageCode || '').trim()
  const normalizedLanguage = rawLanguage.toLowerCase()
  const baseLanguage = normalizedLanguage.split('-')[0]

  const exactMatch = langOptions.find(
    item => String(item.code).toLowerCase() === normalizedLanguage
  )
  if (exactMatch) return exactMatch.code

  const baseMatch = langOptions.find(
    item => String(item.code).toLowerCase() === baseLanguage
  )
  if (baseMatch) return baseMatch.code

  const nameMatch = langOptions.find(
    item => String(item.name).toLowerCase() === normalizedLanguage
  )
  if (nameMatch) return nameMatch.code

  const englishMatch = langOptions.find(
    item => String(item.code).toLowerCase() === 'en'
  )
  return englishMatch ? englishMatch.code : 'en'
}

class CloneModal extends Component {
  constructor(props) {
    super(props)
    const selectedProvider = getDefaultAudioServiceProvider(props.user)
    this.state = {
      isTranslaton: props.isTranslaton,
      isCheckAll: true,
      courseTitle: '',
      isCloning: false,
      courseId: null,
      isMazeClone: false,
      mazeTitle: '',
      mazeId: null,
      isCoverLoading: false,
      modalOpen: false,
      isGenerateAudio: false,
      isSubmittingAudioTranslation: false,
      audioTranslationRequestSent: false,
      selectedProvider,
      selectedVoiceId: null,
      selectedAudioLanguageCode: null,
      elevenLabsVoices: [],
      sharelookVoices: [],
      sharelookVoiceCache: {},
      sharelookVoiceLanguage: null,
      voiceFilters: {},
      isVoiceFiltersLoading: false,
      isVoicesLoading: false
    }
    this.imageRef = createRef()
  }

  componentDidMount() {
    const { isTranslaton } = this.state
    if (isTranslaton && this.state.selectedProvider) {
      this.loadVoicesForProvider(this.state.selectedProvider)
    }
  }

  componentDidUpdate(prevProps) {
    const previousProviders = getAudioServiceProviders(prevProps.user)
    const nextProviders = getAudioServiceProviders(this.props.user)
    const providerListChanged =
      previousProviders.join('|') !== nextProviders.join('|')
    const nextProvider = nextProviders.includes(this.state.selectedProvider)
      ? this.state.selectedProvider
      : nextProviders[0] || null

    if (providerListChanged && this.state.selectedProvider !== nextProvider) {
      this.setState(
        {
          selectedProvider: nextProvider,
          selectedVoiceId: null,
          sharelookVoiceLanguage: null
        },
        () => {
          if (this.state.isTranslaton && this.state.isGenerateAudio) {
            this.loadVoicesForProvider(nextProvider)
          }
        }
      )
    }
  }

  getAudioProviderOptions = () => {
    const allowedProviders = getAudioServiceProviders(this.props.user)
    return AUDIO_PROVIDER_OPTIONS.filter(option =>
      allowedProviders.includes(option.key)
    )
  }

  loadVoicesForProvider = provider => {
    if (!provider) return

    if (provider === 'elevenlabs') {
      this.fetchElevenLabsVoices()
      return
    }

    this.fetchVoiceFilters()
    const languageCode = this.ensureSupportedTtsLanguage(
      this.getTextLanguageCode()
    )
    this.fetchSharelookVoices(languageCode)
  }

  fetchVoiceFilters = async () => {
    const { voiceFilters, isVoiceFiltersLoading } = this.state
    if (isVoiceFiltersLoading || (voiceFilters && voiceFilters.language)) return

    this.setState({
      isVoiceFiltersLoading: true
    })

    try {
      const response = await api.aiVideoGeneration.getVoiceFilters()
      const filters =
        response && response.data && response.data.filters
          ? response.data.filters
          : {}

      this.setState(
        {
          voiceFilters: filters,
          isVoiceFiltersLoading: false
        },
        () => {
          const languageCode = this.ensureSupportedTtsLanguage(
            this.getTextLanguageCode()
          )
          if (this.state.selectedProvider === 'generate-tts-v2') {
            this.fetchSharelookVoices(languageCode)
          }
        }
      )
    } catch (error) {
      this.setState({
        voiceFilters: {},
        isVoiceFiltersLoading: false
      })
    }
  }

  fetchElevenLabsVoices = async () => {
    const { elevenLabsVoices, isVoicesLoading } = this.state
    if (isVoicesLoading) return
    if (elevenLabsVoices.length > 0) {
      if (this.state.selectedProvider === 'elevenlabs') {
        this.ensureSupportedPremiumLanguage(this.getTextLanguageCode())
      }
      return
    }

    this.setState({
      isVoicesLoading: true
    })

    try {
      const response = await api.audioSynthesizer.getElevenLabsVoices()
      const voices =
        response && response.data && Array.isArray(response.data.voices)
          ? response.data.voices
          : []

      this.setState(
        {
          elevenLabsVoices: voices,
          isVoicesLoading: false
        },
        () => {
          if (this.state.selectedProvider === 'elevenlabs') {
            this.ensureSupportedPremiumLanguage(this.getTextLanguageCode())
          }
        }
      )
    } catch (error) {
      this.setState({
        elevenLabsVoices: [],
        isVoicesLoading: false
      })
      message.error(`Failed to fetch voice list. Please try again.`)
    }
  }

  fetchSharelookVoices = async languageCode => {
    const { data, app } = this.props
    const {
      isVoicesLoading,
      sharelookVoiceCache,
      sharelookVoiceLanguage
    } = this.state
    const selectedLanguageCode =
      languageCode ||
      this.state.selectedAudioLanguageCode ||
      (app && app.state && app.state.courseCloneLang) ||
      (data && data.courseLang) ||
      'en'
    const language = this.getLanguageName(selectedLanguageCode)
    const cacheKey = language || selectedLanguageCode

    if (sharelookVoiceCache[cacheKey]) {
      this.setState({
        sharelookVoices: sharelookVoiceCache[cacheKey],
        sharelookVoiceLanguage: cacheKey
      })
      return
    }

    if (isVoicesLoading || sharelookVoiceLanguage === cacheKey) {
      return
    }

    this.setState({
      isVoicesLoading: true,
      sharelookVoices: []
    })

    try {
      const response = await api.aiVideoGeneration.listAIVoices({
        language,
        sort_by: 'audio_quality_score',
        sort_order: 'desc'
      })
      const voices = await this.resolveVoicesResponse(response)
      this.setState(prevState => ({
        sharelookVoices: voices,
        sharelookVoiceCache: {
          ...prevState.sharelookVoiceCache,
          [cacheKey]: voices
        },
        sharelookVoiceLanguage: cacheKey,
        isVoicesLoading: false
      }))
    } catch (error) {
      this.setState({
        sharelookVoices: [],
        isVoicesLoading: false
      })
      message.error(`Failed to fetch voice list. Please try again.`)
    }
  }

  resolveVoicesResponse = async response => {
    const data = response && response.data ? response.data : {}
    const directOutput = data.output || data

    if (
      directOutput &&
      (Array.isArray(directOutput.system_voices) ||
        Array.isArray(directOutput.user_voices) ||
        Array.isArray(directOutput.voices) ||
        Array.isArray(directOutput.results) ||
        Array.isArray(directOutput.items) ||
        Array.isArray(directOutput.data) ||
        Array.isArray(directOutput))
    ) {
      return this.normalizeSharelookVoices(directOutput)
    }

    const taskId =
      data.task_id ||
      data.taskId ||
      data.id ||
      (data.output && data.output.task_id)
    if (!taskId) return []

    const taskData = await this.pollTaskStatus(taskId)
    const output = taskData && taskData.output ? taskData.output : taskData
    return this.normalizeSharelookVoices(output)
  }

  normalizeSharelookVoices = output => {
    if (!output) return []
    const directVoices = Array.isArray(output.voices)
      ? output.voices
      : Array.isArray(output.results)
        ? output.results
        : Array.isArray(output.items)
          ? output.items
          : Array.isArray(output.data)
            ? output.data
            : Array.isArray(output)
              ? output
              : null

    if (directVoices) {
      return directVoices
        .filter(voice => voice && (voice.voice_id || voice.id))
        .map(voice => ({
          ...voice,
          voice_id: voice.voice_id || voice.id,
          name:
            voice.name ||
            voice.voice_name ||
            voice.description ||
            voice.voice_id ||
            voice.id,
          preview_url: voice.preview_url || voice.audio_url
        }))
    }

    const systemVoices = Array.isArray(output.system_voices)
      ? output.system_voices
      : []
    const userVoices = Array.isArray(output.user_voices)
      ? output.user_voices
      : []
    return [...systemVoices, ...userVoices]
      .filter(voice => voice && voice.voice_id)
      .map(voice => ({
        ...voice,
        name:
          voice.name || voice.voice_name || voice.description || voice.voice_id,
        preview_url: voice.preview_url || voice.audio_url
      }))
  }

  pollTaskStatus = async (taskId, delay = 1500, onProgress) => {
    while (true) {
      const statusResponse = await api.aiVideoGeneration.getTaskStatus(taskId)
      const statusData = statusResponse && statusResponse.data
      const statusValue =
        statusData && statusData.status
          ? String(statusData.status).toLowerCase()
          : ''
      if (typeof onProgress === 'function') {
        onProgress(statusData)
      }

      if (
        statusValue === 'success' ||
        statusValue === 'completed' ||
        statusValue === 'done'
      ) {
        return statusData
      }

      if (statusValue === 'failed' || statusValue === 'error') {
        throw new Error('Task failed')
      }

      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }

  handleTitle = _.debounce(v => this.handleTitleChange(v), 1000)

  handleTranslateLanguage = language => {
    const { app } = this.props
    app.setState({
      courseCloneLang: language
    })

    const audioLanguage = this.getSupportedAudioLanguage(language)
    this.setState(
      {
        selectedAudioLanguageCode: audioLanguage,
        selectedVoiceId: null,
        sharelookVoiceLanguage: null
      },
      () => {
        if (
          this.state.isGenerateAudio &&
          this.state.selectedProvider === 'generate-tts-v2'
        ) {
          this.fetchSharelookVoices(audioLanguage)
        }
      }
    )
  }

  handleAudioLanguageChange = language => {
    this.setState(
      {
        selectedAudioLanguageCode: language,
        selectedVoiceId: null,
        sharelookVoiceLanguage: null
      },
      () => {
        if (
          this.state.isGenerateAudio &&
          this.state.selectedProvider === 'generate-tts-v2'
        ) {
          this.fetchSharelookVoices(language)
        }
      }
    )
  }
  handleTitleChange = (v = '') => {
    const { data, app } = this.props

    if (data.type === 'Maze') {
      this.setState({ mazeTitle: v })
      return
    }

    const newTemple = _.cloneDeep(data)

    if (!newTemple) return

    newTemple.courseTitle = v
    app.setState({
      courseCloneTemplate: newTemple
    })
  }

  handleGenerateAudioChange = e => {
    const checked = e.target.checked
    this.setState({
      isGenerateAudio: checked,
      selectedVoiceId: checked ? this.state.selectedVoiceId : null
    })

    if (checked) {
      this.loadVoicesForProvider(this.state.selectedProvider)
    }
  }

  handleProviderChange = provider => {
    const allowedProviders = getAudioServiceProviders(this.props.user)
    if (!allowedProviders.includes(provider)) return

    this.setState(
      {
        selectedProvider: provider,
        selectedVoiceId: null
      },
      () => {
        this.loadVoicesForProvider(provider)
      }
    )
  }

  handleVoiceChange = voiceId => {
    this.setState({
      selectedVoiceId: voiceId
    })
  }

  getLanguageName = languageCode => {
    const match = langOptions.find(item => item.code === languageCode)
    if (match && match.name) {
      return match.name.toLowerCase()
    }
    return languageCode || ''
  }

  getLanguageCode = languageName => {
    const normalizedLanguage = String(languageName || '').toLowerCase()
    const match = langOptions.find(
      item => item.name && item.name.toLowerCase() === normalizedLanguage
    )
    return match ? match.code : languageName
  }

  getTtsLanguageOptions = () => {
    const { voiceFilters } = this.state
    const languages =
      voiceFilters && Array.isArray(voiceFilters.language)
        ? voiceFilters.language
        : []

    return languages.map(language => {
      const normalizedLanguage = String(language || '')
      const name = normalizedLanguage
        .split('-')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
      return {
        name,
        code: this.getLanguageCode(normalizedLanguage)
      }
    })
  }

  getTextLanguageCode = () => {
    const { app, data } = this.props
    return getSupportedTextLanguageCode(
      (app && app.state && app.state.courseCloneLang) ||
      (data && data.courseLang) ||
      'en'
    )
  }

  getPreferredSupportedAudioLanguage = (options, preferredLanguageCode) => {
    if (!options.length) return preferredLanguageCode || 'en'

    const preferred = String(preferredLanguageCode || '').toLowerCase()
    const exactMatch = options.find(
      item => String(item.code).toLowerCase() === preferred
    )
    if (exactMatch) return exactMatch.code

    const englishMatch = options.find(
      item => String(item.code).toLowerCase() === 'en'
    )
    if (englishMatch) return englishMatch.code

    return options[0].code
  }

  getSupportedAudioLanguage = preferredLanguageCode => {
    const options =
      this.state.selectedProvider === 'elevenlabs'
        ? this.getPremiumLanguageOptions()
        : this.getTtsLanguageOptions()

    return this.getPreferredSupportedAudioLanguage(
      options,
      preferredLanguageCode || this.getTextLanguageCode()
    )
  }

  getLanguageOptionName = languageCode => {
    const normalizedLanguage = String(languageCode || '').toLowerCase()
    const languageNameOverrides = {
      zh: 'Chinese'
    }
    if (languageNameOverrides[normalizedLanguage]) {
      return languageNameOverrides[normalizedLanguage]
    }

    const exactMatch = langOptions.find(
      item => String(item.code).toLowerCase() === normalizedLanguage
    )
    if (exactMatch) return exactMatch.name

    const baseCode = normalizedLanguage.split('-')[0]
    const baseMatch = langOptions.find(
      item => String(item.code).toLowerCase() === baseCode
    )
    if (baseMatch) return baseMatch.name

    return String(languageCode || '')
      .split('-')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  }

  getPremiumLanguageOptions = () => {
    const { elevenLabsVoices } = this.state
    const languageMap = {}

    elevenLabsVoices.forEach(voice => {
      if (!Array.isArray(voice.verified_languages)) return

      voice.verified_languages.forEach(languageItem => {
        const language =
          typeof languageItem === 'string'
            ? languageItem
            : languageItem &&
            (languageItem.language ||
              languageItem.code ||
              languageItem.locale)
        if (!language) return

        const code = String(language).toLowerCase()
        if (!languageMap[code]) {
          languageMap[code] = {
            code,
            name: this.getLanguageOptionName(code)
          }
        }
      })
    })

    return Object.values(languageMap).sort((a, b) =>
      a.name.localeCompare(b.name)
    )
  }

  ensureSupportedTtsLanguage = preferredLanguageCode => {
    const ttsLanguageOptions = this.getTtsLanguageOptions()
    if (!ttsLanguageOptions.length) return null

    const selectedLanguageCode =
      preferredLanguageCode ||
      this.state.selectedAudioLanguageCode ||
      this.getTextLanguageCode()
    const nextLanguageCode = this.getPreferredSupportedAudioLanguage(
      ttsLanguageOptions,
      selectedLanguageCode
    )
    this.setState({
      selectedAudioLanguageCode: nextLanguageCode,
      selectedVoiceId: null,
      sharelookVoiceLanguage: null
    })
    return nextLanguageCode
  }

  ensureSupportedPremiumLanguage = preferredLanguageCode => {
    const premiumLanguageOptions = this.getPremiumLanguageOptions()
    if (!premiumLanguageOptions.length) return null

    const selectedLanguageCode =
      preferredLanguageCode ||
      this.state.selectedAudioLanguageCode ||
      this.getTextLanguageCode()
    const nextLanguageCode = this.getPreferredSupportedAudioLanguage(
      premiumLanguageOptions,
      selectedLanguageCode
    )
    this.setState({
      selectedAudioLanguageCode: nextLanguageCode,
      selectedVoiceId: null
    })
    return nextLanguageCode
  }

  checkFinish = () => {
    const { mazeId } = this.state
    const { data } = this.props
    if (data && data.type === 'Maze') {
      console.log('checkfinishclone', mazeId ? true : false)
      return mazeId ? true : false
    } else {
      const { courseId, isTranslaton } = this.state
      const { courseCloningProgress } = this.props
      if (isTranslaton && !courseId) {
        return false
      }
      console.log(
        'checkfinishclone',
        courseCloningProgress && courseCloningProgress.percent === 100
      )
      return courseCloningProgress && courseCloningProgress.percent === 100
    }
  }

  handleCancel = (isHide = false) => {
    const { app, clearCourseCloning } = this.props
    const { courseId } = this.state
    if (courseId && app) {
      clearCourseCloning()
      app.fetchData(true)
    }
    this.setState({
      isCloning: false,
      isSubmittingAudioTranslation: false,
      audioTranslationRequestSent: false,
      courseId: null
    })
    if (app) {
      app.setState({
        isClone: false,
        courseCloneTemplate: null
      })
    }
  }

  onDrop = file => {
    const image = URL.createObjectURL(file)
    this.setState({
      file,
      image
    })
  }

  handleClone = () => {
    const { app, clearCourseCloning, data } = this.props
    const { courseId, mazeId, isTranslaton } = this.state

    const isMaze = data && data.type === 'Maze'

    if (isTranslaton && !courseId) {
      this.translateAPI()
    } else if (isMaze && !mazeId) {
      this.cloneMaze()
    } else if (!isMaze && !courseId) {
      this.cloneAPI()
    } else if (courseId) {
      clearCourseCloning()
      if (app) {
        app.props.history.push(`/courses/${courseId}/edit`)
      }
    } else if (mazeId) {
      if (app) {
        app.props.history.push(`/maze/${mazeId}/edit`)
      }
    }
  }

  cloneMaze = () => {
    const {
      data: { id, title }
    } = this.props
    const { mazeTitle, cover } = this.state

    const obj = {
      body: {
        title: mazeTitle || title,
        ...(cover && {
          cover_id: cover.id
        })
      },
      params: {
        programme_id: id
      },
      action: 'clone_programme'
    }

    this.setState({ isCloning: true })

    api.maze.mazeProvider(obj).then(res => {
      if (res) {
        this.setState({ mazeId: res.data.id })
      }
    })
  }

  cloneAPI = () => {
    const {
      data: { courseTitle, courseId, courseLessons },
      app
    } = this.props

    if (courseTitle.length > 0) {
      const customData = {
        title: courseTitle.substring(0, 90),
        lesson_ids: _.map(
          _.filter(courseLessons, lesson => lesson.isChecked),
          item => item.id
        )
      }
      if (app) {
        app.setState({
          courseCloneTemplate: null
        })
      }
      this.setState({
        isCloning: true,
        courseTitle
      })
      api.courses.cloneCourse(courseId, customData).then(({ data }) => {
        this.setState({
          courseId: data.id,
          courseTitle: data.title
        })
      })
    }
  }
  translateAPI = () => {
    const {
      data: { courseTitle, courseId, courseLessons },
      clearCourseCloning,
      app
    } = this.props
    const {
      isGenerateAudio,
      selectedVoiceId,
      selectedAudioLanguageCode
    } = this.state
    const shouldGenerateAudio = !!(isGenerateAudio || selectedVoiceId)
    const selectedLang = this.getTextLanguageCode()
    const selectedLanguage = this.getLanguageName(selectedLang)
    const audioLanguageCode =
      selectedAudioLanguageCode || this.getSupportedAudioLanguage(selectedLang)
    const audioLanguage = this.getLanguageName(audioLanguageCode)

    if (shouldGenerateAudio && !selectedVoiceId) {
      message.error(this.props.t('v4:voice_must_be_selected'))
      return
    }

    if (courseTitle.length > 0) {
      const customData = {
        title: courseTitle.substring(0, 90),
        isTranslate: true,
        lang: selectedLang,
        language: selectedLanguage,
        isGenerateAudio: shouldGenerateAudio,
        ...(shouldGenerateAudio &&
          selectedVoiceId && {
          provider: this.state.selectedProvider,
          voice_id: selectedVoiceId,
          audio_lang: audioLanguageCode,
          audio_language: audioLanguage
        }),
        lesson_ids: _.map(
          _.filter(courseLessons, lesson => lesson.isChecked),
          item => item.id
        )
      }
      let courseDaa = app.state.courseCloneTemplate
      if (app) {
        app.setState({
          courseCloneTemplate: null
        })
      }

      if (typeof clearCourseCloning === 'function') {
        clearCourseCloning()
      }

      if (shouldGenerateAudio) {
        this.setState({
          isCloning: false,
          isSubmittingAudioTranslation: true,
          audioTranslationRequestSent: false,
          courseId: null,
          courseTitle
        })
      } else {
        this.setState({
          isCloning: true,
          isSubmittingAudioTranslation: false,
          audioTranslationRequestSent: false,
          courseId: null,
          courseTitle
        })
      }

      api.courses
        .cloneCourse(courseId, customData)
        .then(({ data }) => {
          if (shouldGenerateAudio) {
            this.setState(
              {
                isCloning: false,
                isSubmittingAudioTranslation: false,
                audioTranslationRequestSent: true,
                courseId: null,
                courseTitle: data && data.title ? data.title : courseTitle
              },
              () => {
                if (typeof clearCourseCloning === 'function') {
                  clearCourseCloning()
                }
              }
            )
            return
          }

          this.setState({
            courseId: data.id,
            courseTitle: data.title
          })
        })
        .catch(error => {
          if (app) {
            app.setState({
              courseCloneTemplate: {
                ...courseDaa,
                courseLang: app.state.courseCloneLang
              }
            })
          }
          if (typeof clearCourseCloning === 'function') {
            clearCourseCloning()
          }
          this.setState({
            isCloning: false,
            isSubmittingAudioTranslation: false,
            audioTranslationRequestSent: false,
            courseTitle
          })
          let errorMessage = 'Failed to translate, please try again!'

          if (
            error &&
            error.response &&
            error.response.data &&
            error.response.data.errorMessage
          ) {
            errorMessage = error.response.data.errorMessage
          } else if (error && error.message) {
            errorMessage = error.message
          }

          message.error(errorMessage)

          this.setState({
            isCloning: false,
            isSubmittingAudioTranslation: false
          })
        })
    }
  }

  handleCheckAll = (recall = false) => {
    if (recall) {
      const { data } = this.props

      const isUnchecked = _.filter(
        data.courseLessons,
        lesson => !lesson.isChecked
      )

      this.setState({
        isCheckAll: _.isEmpty(isUnchecked)
      })
    } else {
      const { isCheckAll } = this.state

      this.setState(
        {
          isCheckAll: !isCheckAll
        },
        () => {
          this.handleCheckGroup(isCheckAll)
        }
      )
    }
  }

  handleCheckOne = ({ id, isChecked }) => {
    const { data, app } = this.props
    const newTemple = _.cloneDeep(data)

    _.map(newTemple.courseLessons, lesson => {
      if (lesson.id === id) {
        lesson.isChecked = !isChecked
      }
    })

    if (app) {
      app.setState(
        {
          courseCloneTemplate: newTemple
        },
        () => {
          this.handleCheckAll(true)
        }
      )
    }
  }

  handleCheckGroup = isCheckAll => {
    const { data, app } = this.props
    const newTemple = _.cloneDeep(data)

    _.map(newTemple.courseLessons, lesson => {
      lesson.isChecked = !isCheckAll
    })
    if (app) {
      app.setState({
        courseCloneTemplate: newTemple
      })
    }
  }

  getCloneBtn = () => {
    const { mazeId, isTranslaton, isCloning, courseId } = this.state
    const { data, courseCloningProgress, t } = this.props
    const isMaze = data && data.type === 'Maze'
    if (isTranslaton) {
      if (isCloning && !courseId) {
        return t('v4:translatingCourse')
      }
      if (!courseId) {
        return t('v4:translateModalBtn')
      }
      if (courseCloningProgress) {
        const total = courseCloningProgress.total
        const completed = courseCloningProgress.completed
        if (total && completed && total === completed)
          return 'buttons:edit_course'
        else return t('v4:translatingCourse')
      }
      return t('v4:translateModalBtn')
    }
    if (!isMaze) {
      if (courseCloningProgress) {
        const total = courseCloningProgress.total
        const completed = courseCloningProgress.completed
        if (total && completed && total === completed)
          return 'buttons:edit_course'
        else return t('labels:cloning_course')
      }
    }

    if (isMaze && mazeId) {
      return 'v4:edit_maze'
    }
    return 'buttons:clone'
  }

  subTitleString = title => {
    if (title.length > 30) {
      return `${title.substring(0, 30)}...`
    }
    return title
  }

  getPercent = () => {
    const data = this.props.data
    const { mazeId } = this.state
    if (data && data.type === 'Maze') {
      if (mazeId) return 100
      return 0
    } else {
      const { isTranslaton, courseId } = this.state
      if (isTranslaton && !courseId) {
        return 5
      }
      const { courseCloningProgress } = this.props
      return (
        (courseCloningProgress && courseCloningProgress.percent.toFixed(0)) || 5
      )
    }
  }

  getDisable = (isClone = false) => {
    const { data, courseCloningProgress } = this.props
    const {
      mazeId,
      mazeTitle,
      isCloning,
      isSubmittingAudioTranslation,
      isTranslaton,
      isGenerateAudio,
      selectedVoiceId
    } = this.state
    const isMaze = data && data.type === 'Maze'

    if (isSubmittingAudioTranslation) {
      return true
    }

    if (isClone && isMaze && isCloning && !mazeId) {
      return true
    }

    if (isMaze) {
      if (!mazeTitle) {
        if (data.title) {
          return false
        }
        return true
      }
      return false
    }

    if (!isMaze && data) {
      const isHaveCheck =
        data.courseLessons.filter(item => item.isChecked).length >= 1

      if (isTranslaton && isGenerateAudio && !selectedVoiceId) {
        return true
      }
      return !isHaveCheck
    }

    if (!isMaze && isClone) {
      const total = courseCloningProgress && courseCloningProgress.total
      const completed = courseCloningProgress && courseCloningProgress.completed
      if (total && completed) {
        return total !== completed
      }
      return true
    }

    return this.state.isCloning
  }
  getCancelDisable = (isClone = false) => {
    const { data, courseCloningProgress } = this.props
    const {
      mazeId,
      mazeTitle,
      isCloning,
      isSubmittingAudioTranslation
    } = this.state
    const isMaze = data && data.type === 'Maze'

    if (isSubmittingAudioTranslation) {
      return true
    }

    if (isClone && isMaze && isCloning && !mazeId) {
      return true
    }

    if (isMaze) {
      if (!mazeTitle) {
        if (data.title) {
          return false
        }
        return true
      }
      return false
    }
    if (!isMaze && isClone) {
      const total = courseCloningProgress && courseCloningProgress.total
      const completed = courseCloningProgress && courseCloningProgress.completed
      if (total && completed) {
        return total !== completed
      }
      return true
    }

    return this.state.isCloning
  }

  getCloseAble = () => {
    const { courseCloningProgress } = this.props
    const { loadError } = this.props
    const { audioTranslationRequestSent } = this.state
    if (loadError) {
      return true
    }
    if (audioTranslationRequestSent) {
      return true
    }
    if (courseCloningProgress) {
      return true
    }
    return false
  }

  handleUploadImage = file => {
    const { upload } = this.props
    this.setState({
      isCoverLoading: true
    })
    return upload(file).then(({ id, link }) => {
      this.setState({
        isCoverLoading: false,
        cover: {
          id: id,
          link: link
        }
      })
    })
  }

  handleClosePopup = type => {
    if (type === general.SAVE) {
      this.setState({
        image: null
      })
    } else {
      this.setState({
        image: null,
        file: null
      })
    }
  }

  hideModal = () => {
    this.setState({
      modalOpen: false
    })
  }

  handleFileUpload = files => {
    if (!files) return
    this.setState({ selectedAudio: files[0] })
  }

  handleInsertData = (privateMedia, publicMedia, orgMedia) => {
    let tempData = []
    if (privateMedia) {
      const { rows: privateData } = privateMedia
      privateData.forEach(data => {
        tempData.push(data)
      })
    }

    if (publicMedia) {
      const { rows: publicData } = publicMedia
      publicData.forEach(publicData => {
        tempData.push(publicData)
      })
    }

    if (orgMedia) {
      const { rows: orgData } = orgMedia
      orgData.forEach(orgData => {
        tempData.push(orgData)
      })
    }

    const selectedItem = _.head(tempData.filter(value => value.isSelected))

    this.setState({
      selectedItem
    })
  }

  hidePreviewModal = () => {
    this.setState({
      selectedItem: null
    })
  }

  handleFileInsert = item => {
    const imageEditorInst = this.imageRef.current.imageEditorInst
    const data = imageEditorInst.toDataURL()
    const body = {
      croppedImage: data,
      lastModifiedDate: new Date(),
      name: item.filename,
      size: item.file_size,
      type: item.media_mime_type
    }
    this.setState(
      {
        modalOpen: false,
        selectedItem: null
      },
      () => {
        this.handleUploadImage(body)
      }
    )
  }

  useCanva = type => {
    var script = document.createElement('script')
    const url = 'https://sdk.canva.com/designbutton/v2/api.js'
    script.src = url
    script.onload = () => {
      if (window.Canva && window.Canva.DesignButton) {
        window.Canva.DesignButton.initialize({
          apiKey: config.canvaKey
        }).then(api => {
          api.createDesign({
            design: {
              type: type
            },
            onDesignPublish: ({ exportUrl, designTitle }) => {
              this.handleImageUrl(exportUrl, designTitle, type)
            }
          })
        })
      }
    }
    document.body.appendChild(script)
  }

  handleImageUrl = async (url, title) => {
    let blob = await fetch(url).then(r => r.blob())
    let data = await new Promise(resolve => {
      let reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.readAsDataURL(blob)
    })
    const body = {
      croppedImage: data,
      lastModifiedDate: new Date(),
      name: title,
      size: blob.size,
      type: blob.type
    }
    this.handleUploadImage(body)
  }

  render() {
    const {
      data,
      width,
      visible,
      loadError,
      t,
      form: { getFieldDecorator }
    } = this.props
    const isMaze = data && data.type === 'Maze'

    const {
      isCheckAll,
      courseTitle,
      isCloning,
      mazeTitle,
      selectedItem,
      modalOpen,
      file,
      isCoverLoading,
      image,
      cover,
      isTranslaton,
      isGenerateAudio,
      isSubmittingAudioTranslation,
      audioTranslationRequestSent,
      selectedProvider,
      selectedVoiceId,
      selectedAudioLanguageCode,
      elevenLabsVoices,
      sharelookVoices,
      isVoiceFiltersLoading,
      isVoicesLoading
    } = this.state

    const modalTitle = isTranslaton
      ? t('v4:translateModalTitle')
      : isMaze
        ? 'Clone maze'
        : t('labels:clone_modal')
    const text = isMaze
      ? mazeTitle || (data && data.title)
      : data && data.courseTitle
    const lang = (data && data.courseLang) || 'en'
    if (isMaze) {
      var coverLink = cover
        ? cover.link
        : data && data.cover
          ? data.cover.link
          : ''
    }

    const modal_style = {
      overflow: 'auto',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0
    }

    const isNoLessonSelected =
      !isMaze &&
      data &&
      data.courseLessons.filter(item => item.isChecked).length < 1
    const isVoiceRequiredMissing =
      isTranslaton && isGenerateAudio && !selectedVoiceId
    const textLanguageCode = this.getTextLanguageCode()
    const audioLanguageCode =
      selectedAudioLanguageCode ||
      this.getSupportedAudioLanguage(textLanguageCode)
    const selectedLanguageName = this.getLanguageName(audioLanguageCode)
    const voiceDataSource =
      selectedProvider === 'elevenlabs' ? elevenLabsVoices : sharelookVoices
    const voiceTableData = voiceDataSource
      .filter(voice => voice && voice.voice_id)
      .filter(voice => {
        if (selectedProvider === 'elevenlabs') {
          if (
            !Array.isArray(voice.verified_languages) ||
            !voice.verified_languages.length
          ) {
            return false
          }

          return voice.verified_languages.some(languageItem => {
            const language =
              typeof languageItem === 'string'
                ? languageItem
                : languageItem &&
                (languageItem.language ||
                  languageItem.code ||
                  languageItem.locale)
            const locale =
              typeof languageItem === 'string'
                ? ''
                : languageItem && languageItem.locale
            const normalizedLanguage = String(language || '').toLowerCase()
            const normalizedLocale = String(locale || '').toLowerCase()
            const selectedLanguage = String(audioLanguageCode).toLowerCase()

            return (
              normalizedLanguage === selectedLanguage ||
              normalizedLocale === selectedLanguage ||
              normalizedLanguage.indexOf(`${selectedLanguage}-`) === 0 ||
              normalizedLocale.indexOf(`${selectedLanguage}-`) === 0
            )
          })
        }

        if (selectedProvider !== 'generate-tts-v2' || !voice.language)
          return true

        const voiceLanguages = Array.isArray(voice.language)
          ? voice.language
          : String(voice.language).split(',')
        return voiceLanguages.some(
          language =>
            String(language).toLowerCase() ===
            String(selectedLanguageName).toLowerCase() ||
            String(language).toLowerCase() ===
            String(audioLanguageCode).toLowerCase()
        )
      })
      .map(voice => ({
        ...voice,
        key: voice.voice_id
      }))

    return (
      <Modal
        title={modalTitle}
        width={width}
        className="CloneCourseModal"
        style={ModalStyles}
        centered
        visible={visible}
        footer={null}
        onCancel={() => {
          this.handleCancel(true)
        }}
        closable={this.getCloseAble()}
        maskClosable={false}
      >
        {!data && loadError && (
          <div className="clone-load-error">
            <Result
              status="error"
              title="Unable to load course"
              subTitle={loadError}
              extra={[
                <Button
                  key="close"
                  type="primary"
                  onClick={() => this.handleCancel(true)}
                >
                  Close
                </Button>
              ]}
            />
          </div>
        )}
        {!data &&
          !loadError &&
          !isCloning &&
          !isSubmittingAudioTranslation &&
          !audioTranslationRequestSent && (
            <div className="cloneloading">
              <Spin indicator={antIcon} />
              <span>Loading...</span>
            </div>
          )}
        {(data ||
          isCloning ||
          isSubmittingAudioTranslation ||
          audioTranslationRequestSent) && (
            <div className="wrap-content">
              {((!isMaze && data) || (isMaze && !isCloning)) &&
                !isSubmittingAudioTranslation &&
                !audioTranslationRequestSent && (
                  <div className="modal-section modal-title">
                    <span>{t('labels:Title')}</span>
                    <Form.Item colon={false} required={false}>
                      {getFieldDecorator('title', {
                        rules: [
                          {
                            required: true,
                            message: t('errors:title_course'),
                            max: 100
                          },
                          {
                            pattern: '.{3,}',
                            message: t('errors:min_3char')
                          }
                        ],
                        initialValue: text
                      })(
                        <Input
                          name="title"
                          maxLength={100}
                          className="title-input"
                          onChange={e => {
                            this.handleTitle(e.target.value)
                          }}
                        />
                      )}
                    </Form.Item>
                  </div>
                )}
              {isTranslaton &&
                !isCloning &&
                !isSubmittingAudioTranslation &&
                !audioTranslationRequestSent && (
                  <div className="modal-section">
                    <TranslationSection
                      t={t}
                      getFieldDecorator={getFieldDecorator}
                      lang={lang}
                      langOptions={langOptions}
                      textLanguageCode={textLanguageCode}
                      onTextLanguageChange={this.handleTranslateLanguage}
                      isGenerateAudio={isGenerateAudio}
                      onGenerateAudioChange={this.handleGenerateAudioChange}
                      selectedProvider={selectedProvider}
                      providerOptions={this.getAudioProviderOptions()}
                      onProviderChange={this.handleProviderChange}
                      voiceTableData={voiceTableData}
                      isVoicesLoading={isVoicesLoading}
                      selectedVoiceId={selectedVoiceId}
                      onVoiceSelect={this.handleVoiceChange}
                      selectedLanguageCode={audioLanguageCode}
                      onAudioLanguageChange={this.handleAudioLanguageChange}
                      premiumLanguageOptions={this.getPremiumLanguageOptions()}
                      ttsLanguageOptions={this.getTtsLanguageOptions()}
                      isVoiceFiltersLoading={isVoiceFiltersLoading}
                      courseId={data && data.courseId}
                    />
                  </div>
                )}

              {isTranslaton && isSubmittingAudioTranslation && (
                <div className="modal-section translation-status-section">
                  <Result
                    className="translation-status-result translation-status-result--pending"
                    icon={<Icon type="loading" />}
                    title="Starting translation"
                    subTitle="We're sending your translation request now."
                  />
                </div>
              )}

              {isTranslaton && audioTranslationRequestSent && (
                <div className="modal-section translation-status-section">
                  <Result
                    className="translation-status-result"
                    icon={
                      <Icon type="mail" className="translation-status-icon" />
                    }
                    title="Translation has started"
                    subTitle="We'll email you when it's complete. Audio generation may take a few minutes, so you can close this window."
                    extra={[
                      <Button
                        key="close"
                        type="primary"
                        onClick={() => this.handleCancel(true)}
                      >
                        Close
                      </Button>
                    ]}
                  />
                </div>
              )}

              {isMaze && !isCloning && (
                <div className="modal-body">
                  <Row>
                    <Col md={24}>
                      <Form.Item
                        colon={false}
                        required={true}
                        label={t('labels:picture')}
                        className="no_bottom_margin"
                      >
                        <CourseDropzone
                          app={this}
                          onDrop={this.onDrop}
                          error={0}
                          disabled={isCoverLoading}
                          cover={coverLink}
                          loadImage={isCoverLoading}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              )}

              {!isMaze &&
                data &&
                !isSubmittingAudioTranslation &&
                !audioTranslationRequestSent && (
                  <div className="modal-body modal-section section-lessons">
                    <CloningSection
                      t={t}
                      data={data}
                      isCheckAll={isCheckAll}
                      onCheckAll={() => this.handleCheckAll()}
                      onCheckOne={this.handleCheckOne}
                      isNoLessonSelected={isNoLessonSelected}
                      isVoiceRequiredMissing={isVoiceRequiredMissing}
                    />
                  </div>
                )}

              {isCloning &&
                !isSubmittingAudioTranslation &&
                !audioTranslationRequestSent && (
                  <div className="cloning-processing">
                    <div className="processing-orb">
                      <div className="processing-orb-glow" />
                      <Progress
                        strokeLinecap="round"
                        type="circle"
                        percent={this.getPercent()}
                        size={126}
                        strokeColor="#1f78ff"
                        trailColor="#e9edf4"
                        format={percent => `${percent}%`}
                      />
                    </div>
                    {!this.checkFinish() && (
                      <div id="title">
                        <Icon
                          type="loading"
                          className="processing-inline-spinner"
                        />
                        <span>
                          {isTranslaton
                            ? t('v4:translatingCourse')
                            : isMaze
                              ? t('v4:cloning_maze')
                              : t('labels:cloning_course')}
                        </span>
                      </div>
                    )}
                    {!this.checkFinish() && (
                      <span id="desc">
                        {isTranslaton
                          ? t('v4:translationCourseDesc')
                          : t('labels:cloning_course_desc')}
                      </span>
                    )}
                    {this.checkFinish() && (
                      <span id="title-course">
                        {isTranslaton ? (
                          <Trans i18nKey="v4:translationSuccess">
                            {this.subTitleString(courseTitle)}
                          </Trans>
                        ) : isMaze ? (
                          <Trans i18nKey="v4create_certificate:maze_cloning_success">
                            {this.subTitleString(text)}
                          </Trans>
                        ) : (
                          <Trans i18nKey="labels:cloning_success">
                            {this.subTitleString(courseTitle)}
                          </Trans>
                        )}
                      </span>
                    )}
                    <div className="wrap_percent">
                      <Progress
                        percent={this.getPercent()}
                        showInfo={false}
                        strokeColor="#1f78ff"
                        trailColor="#e8edf4"
                        strokeWidth={10}
                      />
                    </div>
                  </div>
                )}

              {!isSubmittingAudioTranslation && !audioTranslationRequestSent && (
                <div className="modal-footer">
                  <Button
                    disabled={this.getCancelDisable()}
                    className={`btn-cancel ${this.getCancelDisable() &&
                      'disabled'}`}
                    onClick={() => this.handleCancel()}
                  >
                    {t('buttons:cancel')}
                  </Button>
                  <Button
                    disabled={this.getDisable(true)}
                    loading={isSubmittingAudioTranslation}
                    className={`btn-clone ${this.getDisable(true) && 'disabled'}`}
                    onClick={() => this.handleClone()}
                  >
                    {t(`${this.getCloneBtn()}`)}
                  </Button>
                </div>
              )}
            </div>
          )}

        {image && (
          <EditImageContainer
            image={image}
            handleUploadImage={this.handleUploadImage}
            handleClosePopup={this.handleClosePopup}
            file={file}
          />
        )}

        {modalOpen && (
          <Modal
            visible={modalOpen}
            onCancel={this.hideModal}
            footer={null}
            width={'80%'}
            style={modal_style}
            centered
          >
            <div className="wrap_modal_tabs">
              <PopupMedia
                isPageBuilder
                handleInsertData={this.handleInsertData}
                types="images"
              />
            </div>
            {selectedItem && (
              <Modal
                visible={selectedItem}
                width="70%"
                onCancel={this.hidePreviewModal}
                footer={null}
                centered
              >
                <Editor library={selectedItem} ref={this.imageRef} />

                <div className="wrap_btn_actions">
                  <Button
                    type="danger"
                    size="large"
                    className="action_button"
                    onClick={this.hidePreviewModal}
                  >
                    {general.CANCEL}
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    className="action_button"
                    onClick={() => this.handleFileInsert(selectedItem)}
                  >
                    {general.INSERT}
                  </Button>
                </div>
              </Modal>
            )}
          </Modal>
        )}
      </Modal>
    )
  }
}

export default Form.create()(withTranslation()(CloneModal))
